/**
 * ============================================================================
 * USE TRANSACTION HOOK
 * ============================================================================
 * 
 * Custom hook for transaction operations
 */

import { useState, useCallback } from 'react';
import * as transactionService from '../services/transactions';

export const useTransaction = (transactionId = null) => {
  const [transaction, setTransaction] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);

  /**
   * Create transaction
   */
  const createTransaction = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.createTransaction(data);
      // Backend returns { success: true, data: { transaction: {...} } }
      return response?.data?.transaction || response?.data || response;
    } catch (err) {
      setError(err.message || 'Failed to create transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get transaction by ID. If 401 (no token), falls back to public-by-ID for payment flow.
   */
  const getTransaction = useCallback(async (id = transactionId) => {
    try {
      setLoading(true);
      setError(null);
      try {
        const response = await transactionService.getTransaction(id);
        const transactionData = response?.data?.transaction || response?.data || response;
        setTransaction(transactionData);
        return transactionData;
      } catch (authErr) {
        if (authErr?.status === 401 && id) {
          const publicResponse = await transactionService.getPublicTransactionById(id);
          const data = publicResponse?.data?.transaction || publicResponse?.data || publicResponse;
          setTransaction(data);
          return data;
        }
        throw authErr;
      }
    } catch (err) {
      setError(err.message || 'Failed to get transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  /**
   * Get public transaction
   */
  const getPublicTransaction = useCallback(async (shortCode) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.getPublicTransaction(shortCode);
      setTransaction(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get public transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * List transactions
   */
  const listTransactions = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.listTransactions(filters);
      // Backend returns { success: true, data: { transactions: [...] } }
      const transactions = response?.data?.transactions || response?.data || [];
      setTransactions(Array.isArray(transactions) ? transactions : []);
      return transactions;
    } catch (err) {
      setError(err.message || 'Failed to list transactions');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update transaction
   */
  const updateTransaction = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.updateTransaction(id, data);
      // Backend returns { success: true, data: { transaction: {...} } }
      const transactionData = response?.data?.transaction || response?.data || response;
      if (id === transactionId) {
        setTransaction(transactionData);
      }
      return transactionData;
    } catch (err) {
      setError(err.message || 'Failed to update transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  /**
   * Cancel transaction
   */
  const cancelTransaction = useCallback(async (id, reason) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.cancelTransaction(id, { reason });
      if (id === transactionId) {
        setTransaction(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to cancel transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  /**
   * Mark as delivered
   */
  const markAsDelivered = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.markAsDelivered(id, data);
      // Backend returns { success: true, data: { transaction: {...} } }
      const transactionData = response?.data?.transaction || response?.data || response;
      if (id === transactionId) {
        setTransaction(transactionData);
      }
      return transactionData;
    } catch (err) {
      setError(err.message || 'Failed to mark as delivered');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  /**
   * Approve delivery
   */
  const approveDelivery = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.approveDelivery(id);
      // Backend returns { success: true, data: { transaction: {...} } }
      const transactionData = response?.data?.transaction || response?.data || response;
      if (id === transactionId) {
        setTransaction(transactionData);
      }
      return transactionData;
    } catch (err) {
      setError(err.message || 'Failed to approve delivery');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  /**
   * Reject delivery
   */
  const rejectDelivery = useCallback(async (id, reason) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.rejectDelivery(id, { reason });
      if (id === transactionId) {
        setTransaction(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to reject delivery');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  /**
   * Request revision
   */
  const requestRevision = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.requestRevision(id, data);
      if (id === transactionId) {
        setTransaction(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to request revision');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  /**
   * Send transaction link
   */
  const sendTransactionLink = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.sendTransactionLink(id, data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to send transaction link');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Accept transaction
   */
  const acceptTransaction = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.acceptTransaction(id);
      if (id === transactionId) {
        setTransaction(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to accept transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  /**
   * Join transaction
   */
  const joinTransaction = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.joinTransaction(id, data);
      if (id === transactionId) {
        setTransaction(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to join transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  /**
   * Get transaction timeline
   */
  const loadTimeline = useCallback(async (id = transactionId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.getTransactionTimeline(id);
      setTimeline(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load timeline');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  /**
   * Get status history
   */
  const loadStatusHistory = useCallback(async (id = transactionId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.getTransactionStatusHistory(id);
      setStatusHistory(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load status history');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  /**
   * Get transaction stats
   */
  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.getTransactionStats();
      setStats(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load stats');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get transaction summary
   */
  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.getTransactionSummary();
      setSummary(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load summary');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Search transactions
   */
  const searchTransactions = useCallback(async (query, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.searchTransactions(query, filters);
      setTransactions(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to search transactions');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get transaction by reference
   */
  const getTransactionByReference = useCallback(async (referenceId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await transactionService.getTransactionByReference(referenceId);
      setTransaction(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get transaction by reference');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    transaction,
    transactions,
    stats,
    summary,
    timeline,
    statusHistory,
    loading,
    error,
    
    // Actions
    createTransaction,
    getTransaction,
    getPublicTransaction,
    listTransactions,
    updateTransaction,
    cancelTransaction,
    markAsDelivered,
    approveDelivery,
    rejectDelivery,
    requestRevision,
    sendTransactionLink,
    acceptTransaction,
    joinTransaction,
    loadTimeline,
    loadStatusHistory,
    loadStats,
    loadSummary,
    searchTransactions,
    getTransactionByReference,
    
    // Convenience
    refresh: transactionId ? () => getTransaction(transactionId) : null,
  };
};

export default useTransaction;
